import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const DetailedItinerary = ({ itinerary }) => {
  const [expandedDay, setExpandedDay] = useState(null);

  const toggleDay = (dayIndex) => {
    setExpandedDay(expandedDay === dayIndex ? null : dayIndex);
  };

  if (!itinerary?.length) return null;

  return (
    <div className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-accent">Detailed</span> Itinerary
          </h2>
          <div className="w-16 h-1 bg-accent rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Every day of your trek is carefully planned to ensure proper
            acclimatization, cultural immersion, and unforgettable experiences
            in the Himalayas.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-3">
          {itinerary?.map((day, index) => {
            const isOpen = expandedDay === index;
            return (
              <div
                key={index}
                className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen
                    ? "border-accent/30 shadow-md"
                    : "border-border/50 hover:border-accent/20 hover:shadow-sm"
                }`}
              >
                <div
                  className="p-5 md:p-6 cursor-pointer select-none"
                  onClick={() => toggleDay(index)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-headline font-bold text-lg flex-shrink-0 transition-colors ${
                        isOpen ? "bg-accent text-white" : "bg-primary/10 text-primary"
                      }`}>
                        {day?.day}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-headline font-semibold text-foreground text-lg truncate">
                          {day?.title}
                        </h3>
                        {day?.location && (
                          <p className="text-text-secondary text-sm truncate">{day?.location}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="hidden md:flex items-center gap-3 text-xs text-text-secondary">
                        {day?.walkingTime && (
                          <div className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full">
                            <Icon name="Clock" size={12} />
                            <span>{day?.walkingTime}</span>
                          </div>
                        )}
                        {day?.elevation && (
                          <div className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full">
                            <Icon name="TrendingUp" size={12} />
                            <span>{day?.elevation}</span>
                          </div>
                        )}
                        {day?.distance && (
                          <div className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full">
                            <Icon name="MapPin" size={12} />
                            <span>{day?.distance}</span>
                          </div>
                        )}
                      </div>

                      <Icon
                        name={isOpen ? "ChevronUp" : "ChevronDown"}
                        size={20}
                        className={`text-text-secondary transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-border/50 animate-fade-in">
                    <div className="p-5 md:p-6 space-y-6">
                      <div className="md:hidden flex flex-wrap gap-2 text-xs text-text-secondary">
                        {day?.walkingTime && (
                          <div className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full">
                            <Icon name="Clock" size={12} />
                            <span>{day?.walkingTime}</span>
                          </div>
                        )}
                        {day?.elevation && (
                          <div className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full">
                            <Icon name="TrendingUp" size={12} />
                            <span>{day?.elevation}</span>
                          </div>
                        )}
                        {day?.distance && (
                          <div className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full">
                            <Icon name="MapPin" size={12} />
                            <span>{day?.distance}</span>
                          </div>
                        )}
                      </div>

                      <div className="prose prose-gray max-w-none">
                        <p className="text-text-secondary leading-relaxed">
                          {day?.description}
                        </p>
                      </div>

                      {day?.highlights && day?.highlights?.length > 0 && (
                        <div>
                          <h4 className="font-headline font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="Star" size={16} className="text-accent" />
                            Day Highlights
                          </h4>
                          <div className="grid sm:grid-cols-2 gap-2">
                            {day?.highlights?.map((highlight, idx) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 p-2.5 bg-[#fafaf7] rounded-lg"
                              >
                                <Icon
                                  name="Check"
                                  size={14}
                                  className="text-success mt-0.5 flex-shrink-0"
                                />
                                <span className="text-sm text-text-secondary">
                                  {highlight}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="grid md:grid-cols-2 gap-4">
                        {day?.accommodation && (
                          <div className="flex items-start gap-3 p-4 bg-muted rounded-xl">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Home" size={16} className="text-primary" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-foreground text-sm mb-1">
                                Accommodation
                              </h4>
                              <p className="text-sm text-text-secondary">
                                {day?.accommodation}
                              </p>
                            </div>
                          </div>
                        )}

                        {day?.meals && (
                          <div className="flex items-start gap-3 p-4 bg-muted rounded-xl">
                            <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Utensils" size={16} className="text-accent" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-foreground text-sm mb-1">
                                Meals
                              </h4>
                              <p className="text-sm text-text-secondary">
                                {day?.meals}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DetailedItinerary;
